import React,{useEffect, useState} from 'react';
import { motion } from 'framer-motion';
import { FaBuilding, FaHome, FaLandmark, FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';

// Import images (you'll need to add these to your assets)
import aboutHero from '../assets/images/about-hero.jpg';
import teamMember1 from '../assets/images/team1.jpg';
import teamMember2 from '../assets/images/team2.png';
import teamMember3 from '../assets/images/team3.png';

const About = () => {
  const[team,setTeamMembers]=useState([])

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        // Fetch team members from real estate team API
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/get_all_members`
        );
        const data = await response.json();
        
        setTeamMembers(data.data || []);
      } catch (error) {
        console.error("Error fetching team members:", error);
      }
    };

    fetchTeamMembers();
  }, []);


  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative h-[40vh] sm:h-[50vh] md:h-[60vh] bg-cover bg-center" style={{ backgroundImage: `url(${aboutHero})` }}>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-black/80"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-white"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-6">About Us</h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-2xl">
              Your trusted partner in real estate excellence, dedicated to providing exceptional property solutions and personalized service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-yellow-500 font-semibold mb-2 sm:mb-4 block">Our Promise</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-8 text-gray-800">
                Excellence in Real Estate Solutions
              </h2>
              <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                We are committed to providing exceptional real estate services with integrity, expertise, and personalized attention.
              </p>
              <div className="grid grid-cols-2 gap-3 sm:gap-6">
                {[
                  { number: '1000+', label: 'Properties Managed' },
                  { number: '500+', label: 'Happy Clients' },
                  { number: '₹100Cr+', label: 'Property Value' },
                  { number: '20+', label: 'Years Experience' }
                ].map((stat, index) => (
                  <div key={index} className="bg-white p-3 sm:p-6 rounded-xl shadow-lg">
                    <div className="text-xl sm:text-2xl font-bold text-yellow-500">{stat.number}</div>
                    <div className="text-sm sm:text-base text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-3 sm:gap-6"
            >
              {[
                { icon: FaHome, title: 'Residential Expertise' },
                { icon: FaLandmark, title: 'Commercial Solutions' },
                { icon: FaBuilding, title: 'Property Management' },
                { icon: FaMapMarkerAlt, title: 'Local Market Knowledge' }
              ].map((feature, index) => (
                <div key={index} className="bg-white p-4 sm:p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <feature.icon className="text-2xl sm:text-4xl text-yellow-500 mb-2 sm:mb-4" />
                  <h3 className="text-sm sm:text-lg font-semibold text-gray-800">{feature.title}</h3>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-16"
          >
            <span className="text-yellow-400 font-semibold mb-2 sm:mb-4 block">Our Team</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-6">Our Expert Real Estate Team</h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
              Meet our dedicated professionals who bring years of real estate expertise to every transaction.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="group relative overflow-hidden rounded-2xl bg-gradient-to-b from-gray-800 to-gray-900"
              >
                <div className="aspect-w-3 aspect-h-4">
                  <img 
                    src={member.photo} 
                    alt={member.fullName} 
                    className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-500" 
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                  <div className="absolute bottom-0 p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">{member.fullName}</h3>
                    <p className="text-yellow-400 text-sm sm:text-base">{member.designation}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-yellow-500 font-semibold mb-2 sm:mb-4 block">Get In Touch</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-8 text-gray-800">Visit Our Office</h2>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-start">
                    <FaMapMarkerAlt className="text-yellow-500 text-xl sm:text-2xl mt-1 mr-3 sm:mr-4" />
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Office Address</h3>
                      <p className="text-gray-600 text-sm sm:text-base">567, Shaha Urf Pipalgaon, Near IIIT-A Chauraha, Jhalwa, Prayagraj 211012</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <FaPhone className="text-yellow-500 text-xl sm:text-2xl mt-1 mr-3 sm:mr-4" />
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Phone</h3>
                      <p className="text-gray-600 text-sm sm:text-base">+91 9555562580 , +91 6394342072</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <FaEnvelope className="text-yellow-500 text-xl sm:text-2xl mt-1 mr-3 sm:mr-4" />
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Email</h3>
                      <p className="text-gray-600 text-sm sm:text-base">prayagproperties2020@gmail.com , mishraconstruction2904@gmail.com</p>
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="h-[250px] sm:h-[300px] md:h-[400px] rounded-xl overflow-hidden mt-6 md:mt-0"
              >
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d1801.627566092389!2d81.76728193837714!3d25.429732516729665!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1s%20567%20Saha%20Urf%20Pipalgaon%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20Near%20IIIT-A%20Chauraha%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20211012!5e0!3m2!1sen!2sin!4v1743835419597!5m2!1sen!2sin" 
                  width="100%" 
                  height="100%" 
                  style={{border:0}} 
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;